import { Controller } from 'react-hook-form';
import { faCheck } from '@fortawesome/pro-solid-svg-icons';
import Alert from '@v2/shared/components/Alert';
import Button from '@v2/shared/components/Button';
import { FormInput, FormInputPhone, FormTextArea } from '@v2/shared/components/Form';
import useUrlGenerator from '@v2/shared/lib/useUrlGenerator';
import useQuoteForm from './useQuoteForm';
import { ERROR_MESSAGES } from './constants';

export default function QuoteForm() {
  const { buildUrl } = useUrlGenerator();
  const {
    register,
    handleSubmit,
    control,
    errors,
    onSubmit,
    submitState,
    submitError,
    isSubmitting,
  } = useQuoteForm();

  if (submitState === 'success') {
    return (
      <Alert color="success">
        <p className="font-semibold text-base mb-1">Request received!</p>
        <p>
          Thanks for reaching out. We&apos;ll get back to you within 1 business day with a custom
          quote.
        </p>
      </Alert>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate>
      {submitState === 'error' && submitError && (
        <Alert color="danger">{ERROR_MESSAGES[submitError]}</Alert>
      )}

      <FormInput
        id="name"
        label="Your Name"
        type="text"
        placeholder="John Doe"
        disabled={isSubmitting}
        error={errors.name?.message}
        {...register('name', { required: 'Name is required' })}
      />

      <FormInput
        id="company"
        label="Company / School"
        type="text"
        placeholder="Business Name"
        disabled={isSubmitting}
        error={errors.company?.message}
        {...register('company', { required: 'Company or school name is required' })}
      />

      <FormInput
        id="email"
        label="Email Address"
        type="email"
        placeholder="john@company.com"
        disabled={isSubmitting}
        error={errors.email?.message}
        {...register('email', {
          required: 'Email address is required',
          pattern: {
            value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
            message: 'Please enter a valid email address',
          },
        })}
      />

      <Controller
        name="phone"
        control={control}
        rules={{
          required: 'Phone number is required',
          validate: (value) =>
            value?.replace(/\D/g, '').length === 10 ||
            'Please enter a valid 10-digit phone number',
        }}
        render={({ field, fieldState }) => (
          <FormInputPhone
            id="phone"
            label="Phone Number"
            placeholder="(555) 555-5555"
            value={field.value}
            onChange={field.onChange}
            onBlur={field.onBlur}
            disabled={isSubmitting}
            error={fieldState.error?.message}
          />
        )}
      />

      <FormTextArea
        id="quantity"
        label="Estimated Quantity / Titles"
        rows={4}
        placeholder="e.g. 10 copies of People, 5 copies of Time"
        disabled={isSubmitting}
        error={errors.quantity?.message}
        {...register('quantity', { required: 'Please describe your quantity and titles' })}
      />

      <Button
        type="submit"
        isLoading={isSubmitting}
        loadingText="Sending…"
        fullWidth
        size="lg"
        shape="rounded"
        icon={faCheck}
        className="mb-3"
      >
        Request Free Quote
      </Button>

      <p className="text-sm text-slate-500 text-center">
        No spam. No obligation. Quotes usually sent by next business day. Bulk orders start at{' '}
        <strong className="font-semibold">10 magazines</strong>. Need fewer?{' '}
        <a href={buildUrl('/')} className="text-red-600">
          Order individual subscriptions
        </a>
        .
      </p>
    </form>
  );
}
